/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { usePagination } from 'src/core/hooks/usePagination';
import { UtteranceWithAnalytics } from 'src/luis/projects/utterances/types/UtteranceWithAnalytics';

type usePaginatedUtterancesParams = {
    utterances: UtteranceWithAnalytics[];
    pageSize: number;
    pageIndex: number;
    totalSize: number;
    isChunking: boolean;
    changePageIndex: (pageIndex: number) => void;
};

/**
 * A hook for managing the pagination in utterances.
 *
 * @param utterances The total items to be managed.
 * @param pageSize The default page size.
 * @param pageIndex The current active page index.
 * @param totalSize The total size of items.
 * @param changePageIndex The method used to load the page items without chunking operation.
 * @param isChunking Determines whether the current mode is chunking or not.
 * @returns The required information to managing the pagination with or without chunking.
 */
export const usePaginatedUtterances = (params: usePaginatedUtterancesParams) => {
    const { utterances, pageSize, pageIndex, totalSize, changePageIndex, isChunking } = params;
    const { currentPage: paginatedCurrentPage, setCurrentPage, pageItems } = usePagination(utterances, pageSize);
    if (isChunking) {
        return {
            items: pageItems,
            pageCount: utterances.length / pageSize,
            currentPage: paginatedCurrentPage,
            onPageChange: setCurrentPage
        };
    }
    return { items: utterances, pageCount: totalSize / pageSize, currentPage: pageIndex, onPageChange: changePageIndex };
};
